import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { DetailPanel } from "./DetailPanel";
import {
  MapPin, Wifi, WifiOff, Package, AlertTriangle, ShoppingBag, Monitor,
  Activity as ActivityIcon, TrendingUp, ChevronRight,
} from "lucide-react";

const money = (n) => `£${Number(n || 0).toFixed(2)}`;
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function PeriodCard({ label, data, highlight }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-indigo-200 bg-indigo-50/60 dark:border-indigo-900 dark:bg-indigo-950/30" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950"}`}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-white">{money(data?.revenue)}</p>
      <p className="text-xs text-slate-400">{data?.count || 0} sale(s)</p>
    </div>
  );
}

function Fact({ icon: Icon, label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-900 dark:text-white", rose: "text-rose-600", emerald: "text-emerald-600" };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className={`mt-1 text-xl font-bold ${tones[tone]}`}>{value}</p>
    </div>
  );
}

export function LocationPanel({ locationId, onClose, onOpenKiosk }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => {
    if (!locationId) { setData(null); return; }
    setLoading(true);
    api.get(`/locations/${locationId}/dashboard`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [locationId]);

  useEffect(() => { setTab("overview"); load(); }, [locationId, load]);

  const tabs = [
    { key: "overview", label: "Overview", icon: ActivityIcon },
    { key: "kiosks", label: "Kiosks", icon: Monitor },
    { key: "transactions", label: "Transactions", icon: ShoppingBag },
    { key: "products", label: "Products", icon: Package },
  ];

  return (
    <DetailPanel
      open={!!locationId}
      onClose={onClose}
      eyebrow="Location"
      title={data?.name || "Location"}
      icon={MapPin}
      width="w-full sm:w-[640px] lg:w-[60%]"
      testid="location-panel"
    >
      {loading && <p className="text-sm text-slate-400" data-testid="location-panel-loading">Loading location…</p>}
      {!loading && !data && <p className="text-sm text-slate-400">Could not load this location.</p>}
      {!loading && data && (
        <div className="space-y-5">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {[data.org_name, data.building_name].filter(Boolean).join("  ·  ") || "—"} · {data.kiosk_count} kiosk(s)
          </p>

          <div className="flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {tabs.map((t) => (
              <button
                key={t.key}
                data-testid={`location-panel-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "overview" && (
            <div className="space-y-5" data-testid="location-panel-overview">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <PeriodCard label="Current week" data={data.stats?.current_week} highlight />
                <PeriodCard label="Previous week" data={data.stats?.previous_week} />
                <PeriodCard label="Month to date" data={data.stats?.mtd} />
                <PeriodCard label="Year to date" data={data.stats?.ytd} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Fact icon={Monitor} label="Kiosks" value={data.kiosk_count} />
                <Fact icon={Package} label="Products" value={data.product_count} />
                <Fact icon={AlertTriangle} label="Low stock" value={data.low_stock} tone={data.low_stock ? "rose" : "slate"} />
                <Fact icon={TrendingUp} label="All-time rev." value={money(data.total_revenue)} tone="emerald" />
              </div>
            </div>
          )}

          {tab === "kiosks" && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800" data-testid="location-panel-kiosks">
              {data.kiosks?.length ? data.kiosks.map((k) => {
                const online = k.status === "online";
                return (
                  <button
                    key={k.id}
                    data-testid={`location-panel-kiosk-${k.code}`}
                    onClick={() => onOpenKiosk?.(k.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900 text-left transition-colors"
                  >
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${online ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                      {online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}{online ? "Online" : "Offline"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{k.name}</p>
                      <p className="text-xs text-slate-400">{k.code} · {k.product_count} products{k.pin_set ? " · PIN" : ""}</p>
                    </div>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">{money(k.revenue)}</span>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </button>
                );
              }) : <p className="px-4 py-3 text-sm text-slate-400 bg-white dark:bg-slate-950">No kiosks at this location.</p>}
            </div>
          )}

          {tab === "transactions" && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800" data-testid="location-panel-transactions">
              {data.recent_transactions?.length ? data.recent_transactions.map((t) => (
                <div key={t.transaction_id} data-testid={`location-panel-txn-${t.transaction_id}`} className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-950">
                  <ShoppingBag className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{t.email || "Guest"} · {t.item_count} item(s)</p>
                    <p className="text-xs text-slate-400">{fmtDateTime(t.created_at)}{t.kiosk_name ? ` · ${t.kiosk_name}` : ""}{t.payment_status !== "paid" ? ` · ${t.payment_status}` : ""}</p>
                  </div>
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">{money(t.gross)}</span>
                </div>
              )) : <p className="px-4 py-3 text-sm text-slate-400 bg-white dark:bg-slate-950">No sales yet.</p>}
            </div>
          )}

          {tab === "products" && (
            <div className="space-y-3" data-testid="location-panel-products">
              <p className="text-xs text-slate-400">Stock totalled across all kiosks at this location. Adjust individual stock from a kiosk.</p>
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
                {data.products?.length ? data.products.map((p) => (
                  <div key={p.product_id} data-testid={`location-panel-product-${p.sku || p.product_id}`} className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-950">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{p.name}</p>
                      <p className="text-xs text-slate-400">{p.sku || "—"} · {money(p.price_incl)} · in {p.kiosk_count} kiosk(s)</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${p.low ? "text-rose-600" : "text-slate-900 dark:text-white"}`}>{p.stock_qty} total</p>
                      {p.low && <p className="text-[10px] font-bold uppercase text-rose-500">Low</p>}
                    </div>
                  </div>
                )) : <p className="px-4 py-3 text-sm text-slate-400 bg-white dark:bg-slate-950">No products.</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </DetailPanel>
  );
}
